import {
  AllergenInput, Button, Card, Field, Input, PhoneInput, Row, Select, Stack, useI18n,
  type AllergySeverity,
} from '@daghfal/core';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { emptyAllergy, emptyContact, type AllergyDraft, type ContactDraft } from './registrationDrafts';

const SEVERITIES: AllergySeverity[] = ['Mild', 'Severe', 'Anaphylactic'];

/**
 * A child's allergy answer (Q9): the allergies, or the family's answer that there are none. Ticking "none
 * known" hides the rows, so the two cannot be sent together; the API refuses that in any case.
 */
export function AllergiesEditor({ allergies, setAllergies, noneKnown, setNoneKnown, title, footer }: {
  allergies: AllergyDraft[];
  setAllergies: Dispatch<SetStateAction<AllergyDraft[]>>;
  noneKnown: boolean;
  setNoneKnown: (value: boolean) => void;
  title: string;
  footer?: ReactNode;
}) {
  const { t } = useI18n();

  return (
    <Card
      title={title}
      action={!noneKnown && (
        <Button variant="secondary" onClick={() => setAllergies((current) => [...current, emptyAllergy()])}>
          {t('children.new.addAllergy')}
        </Button>
      )}
    >
      <Stack>
        <label className="dg-checkbox">
          <input
            type="checkbox"
            checked={noneKnown}
            onChange={(event) => {
              setNoneKnown(event.target.checked);
              if (event.target.checked) setAllergies([]);
            }}
          />
          <span>{t('children.new.noKnownAllergies')}</span>
        </label>
        {!noneKnown && allergies.length === 0 && <p className="dg-card__hint">{t('children.new.allergies.hint')}</p>}
        {!noneKnown && allergies.map((allergy, index) => (
          <Row key={index}>
            <Field label={t('children.new.allergen')} required>
              {(props) => (
                <AllergenInput
                  {...props}
                  value={allergy.allergen}
                  onChange={(allergen) => updateAt(setAllergies, index, { allergen })}
                />
              )}
            </Field>
            <Field label={t('children.new.severity')}>
              {(props) => (
                <Select
                  {...props}
                  value={allergy.severity}
                  onChange={(e) => updateAt(setAllergies, index, { severity: e.target.value as AllergySeverity })}
                >
                  {SEVERITIES.map((severity) => (
                    <option key={severity} value={severity}>{t(`child.severity.${severity}`)}</option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('children.new.actionRequired')}>
              {(props) => (
                <Input
                  {...props}
                  value={allergy.actionRequired}
                  onChange={(e) => updateAt(setAllergies, index, { actionRequired: e.target.value })}
                />
              )}
            </Field>
            <Button variant="ghost" onClick={() => setAllergies((current) => current.filter((_, i) => i !== index))}>
              {t('children.new.remove')}
            </Button>
          </Row>
        ))}
        {footer}
      </Stack>
    </Card>
  );
}

/** Who to call in an emergency, in calling order. At least one is required (Q9). */
export function ContactsEditor({ contacts, setContacts, title, footer }: {
  contacts: ContactDraft[];
  setContacts: Dispatch<SetStateAction<ContactDraft[]>>;
  title: string;
  footer?: ReactNode;
}) {
  const { t } = useI18n();

  return (
    <Card
      title={title}
      action={
        <Button variant="secondary" onClick={() => setContacts((current) => [...current, emptyContact()])}>
          {t('children.new.addContact')}
        </Button>
      }
    >
      <Stack>
        {contacts.length === 0 && <p className="dg-card__hint">{t('children.new.emergencyContacts.hint')}</p>}
        {contacts.map((contact, index) => (
          <Row key={index}>
            <Field label={t('children.new.contactName')} required>
              {(props) => (
                <Input
                  {...props}
                  value={contact.name}
                  onChange={(e) => updateAt(setContacts, index, { name: e.target.value })}
                />
              )}
            </Field>
            <Field label={t('children.new.contactRelationship')}>
              {(props) => (
                <Input
                  {...props}
                  value={contact.relationship}
                  onChange={(e) => updateAt(setContacts, index, { relationship: e.target.value })}
                />
              )}
            </Field>
            <Field label={t('children.new.contactPhone')} required>
              {(props) => (
                <PhoneInput
                  {...props}
                  value={contact.phone}
                  onChange={(e) => updateAt(setContacts, index, { phone: e.target.value })}
                />
              )}
            </Field>
            <Button variant="ghost" onClick={() => setContacts((current) => current.filter((_, i) => i !== index))}>
              {t('children.new.remove')}
            </Button>
          </Row>
        ))}
        {footer}
      </Stack>
    </Card>
  );
}

/** Replaces one row of a draft list without mutating it. */
function updateAt<T>(setter: Dispatch<SetStateAction<T[]>>, index: number, patch: Partial<T>): void {
  setter((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)));
}
